import { reducerWithInitialState } from 'typescript-fsa-reducers'
import { produce } from 'immer'

import {
  DeferredPost,
  DeferredPostSearchResult,
  MediaFileMetadata,
  PostTemplate,
  Tag,
  TopicsWithMetadataResponse, WebResourceMetaData
} from '@3m5/sm_publish-frontend-api/models'
import { PagedMediaFileResponse } from '@3m5/sm_publish-frontend-api'

import { ApiError } from '../../store/postingRoot.reducer'

import {
  createPostTemplate,
  createTags,
  deleteMediaFile,
  deletePostTemplate,
  findPostTemplatesForCurrentUser,
  getAllTags,
  getDeferredPostById,
  getMediaFileMetadata,
  getTopicsAndMetadataForCurrentUser, getUrlInformation,
  individualizeText,
  listDeferredPosts,
  listMediaFiles,
  publishPost, setPostingScreen, setPostingType,
  setUpdateTemplateId,
  setUploadTemporaryMediaFile, setUrlInformation,
  updateDeferredPost,
  updatePagePost,
  updatePostTemplate,
  uploadMediaFile,
  uploadTemporaryMediaFile,
} from './posting.actions'
import { PostTemplatesSearchResult } from '@3m5/sm_publish-frontend-api/models/PostTemplatesSearchResult'
import { PostingScreen, PostingType } from '../../shared/types/types'

export interface PostingState {
  fetching: boolean
  tags?: Tag[]
  templates?: PostTemplatesSearchResult
  deferredPostById?: DeferredPost
  postingType: PostingType
  deferredPosts?: DeferredPostSearchResult
  pageResource?: WebResourceMetaData
  aboTopics?: TopicsWithMetadataResponse
  mediaFile?: MediaFileMetadata
  temporaryMediaFile?: MediaFileMetadata
  individualizedText?: string
  loadingTMFile?: boolean
  mediaFiles?: PagedMediaFileResponse
  updatedPostTemplate?: PostTemplate
  updateTemplateId?: string
  error: ApiError
  postingScreen: PostingScreen;
}

const INITIAL_STATE: PostingState = {
  fetching: false,
  loadingTMFile: false,
  postingType: PostingType.posting,
  error: {},
  postingScreen: PostingScreen.MAIN,
}

const triggerProfessocialPostingSuccess = (data: boolean) => {
  window.dispatchEvent(new CustomEvent('ProfessocialPostingSuccess', { detail: data }))
}

const triggerProfessocialPostingError = (data: boolean) => {
  window.dispatchEvent(new CustomEvent('ProfessocialPostingError', { detail: data }))
}

export const PostingReducer = reducerWithInitialState(INITIAL_STATE)
  .case(setUpdateTemplateId, (state, updateTemplateId) => {
    return ({ ...state, updateTemplateId })
  })
  .case(setPostingType, (state, postingType) => {
    return ({ ...state, postingType })
  })
  .case(setUploadTemporaryMediaFile, (state, temporaryMediaFile) => {
    return ({ ...state, temporaryMediaFile })
  })
  .case(setPostingScreen, (state, postingScreen) => {
    return ({ ...state, postingScreen })
  })

  /**
   * getAllTags
   */
  .case(getAllTags.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getAllTags
    })
  })

  .case(getAllTags.done, (state, { result }) => {
    return produce(state, draft => {
      draft.tags = result
      draft.fetching = false
    })
  })

  .case(getAllTags.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getAllTags = (error as Response)
    })
  })

  /**
   * getTopicsAndMetadataForCurrentUser
   */
  .case(getTopicsAndMetadataForCurrentUser.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getTopicsAndMetadataForCurrentUser
    })
  })

  .case(getTopicsAndMetadataForCurrentUser.done, (state, { result }) => {
    return produce(state, draft => {
      draft.aboTopics = result
      draft.fetching = false
    })
  })

  .case(getTopicsAndMetadataForCurrentUser.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getTopicsAndMetadataForCurrentUser = (error as Response)
    })
  })

  /**
   * listDeferredPosts
   */
  .case(listDeferredPosts.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.listDeferredPosts
    })
  })

  .case(listDeferredPosts.done, (state, { result }) => {
    return produce(state, draft => {
      draft.deferredPosts = result
      draft.fetching = false
    })
  })

  .case(listDeferredPosts.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getAllTags = (error as Response)
    })
  })

  /**
   * findPostTemplatesForCurrentUser
   */
  .case(findPostTemplatesForCurrentUser.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.findPostTemplatesForCurrentUser
    })
  })

  .case(findPostTemplatesForCurrentUser.done, (state, { result }) => {
    return produce(state, draft => {
      draft.templates = result
      draft.fetching = false
    })
  })

  .case(findPostTemplatesForCurrentUser.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.findPostTemplatesForCurrentUser = (error as Response)
    })
  })

  /**
   * publishPost
   */
  .case(publishPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.temporaryMediaFile = undefined
      delete draft.error.publishPost
    })
  })

  .case(publishPost.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      triggerProfessocialPostingSuccess(true)
    })
  })

  .case(publishPost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.publishPost = (error as Response)
      triggerProfessocialPostingError(true)
    })
  })

  /**
   * createPostTemplate
   */
  .case(createPostTemplate.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.temporaryMediaFile = undefined
      delete draft.error.createPostTemplate
    })
  })

  .case(createPostTemplate.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      triggerProfessocialPostingSuccess(true)
    })
  })

  .case(createPostTemplate.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.createPostTemplate = (error as Response)
      triggerProfessocialPostingError(true)
    })
  })

  /**
   * updatePostTemplate
   */
  .case(updatePostTemplate.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.temporaryMediaFile = undefined
      delete draft.error.updatedPostTeplate
    })
  })

  .case(updatePostTemplate.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.updatedPostTemplate = result
      triggerProfessocialPostingSuccess(true)
    })
  })

  .case(updatePostTemplate.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.updatedPostTemplate = (error as Response)
      triggerProfessocialPostingError(true)
    })
  })

  /**
   * deletePostTemplate
   */
  .case(deletePostTemplate.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.deletePostTemplate
    })
  })

  .case(deletePostTemplate.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })

  .case(deletePostTemplate.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.deletePostTemplate = (error as Response)
    })
  })

  /**
   * getMediaFileMetadata
   */
  .case(getMediaFileMetadata.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getMediaFileMetadata
    })
  })

  .case(getMediaFileMetadata.done, (state, { result }) => {
    return produce(state, draft => {
      draft.mediaFile = result
      draft.fetching = false
    })
  })

  .case(getMediaFileMetadata.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getMediaFileMetadata = (error as Response)
    })
  })

  /**
   * uploadMediaFile
   */
  .case(uploadMediaFile.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.uploadMediaFile
    })
  })

  .case(uploadMediaFile.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })

  .case(uploadMediaFile.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.uploadMediaFile = (error as Response)
    })
  })

  /**
   * uploadTemporaryMediaFile
   */
  .case(uploadTemporaryMediaFile.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.temporaryMediaFile = undefined
      draft.loadingTMFile = true
      delete draft.error.uploadTemporaryMediaFile
    })
  })

  .case(uploadTemporaryMediaFile.done, (state, { result }) => {
    return produce(state, draft => {
      draft.temporaryMediaFile = result
      draft.fetching = false
      draft.loadingTMFile = result && false
    })
  })

  .case(uploadTemporaryMediaFile.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.loadingTMFile = false
      draft.error.uploadTemporaryMediaFile = (error as Response)
    })
  })

  /**
   * deleteMediaFile
   */
  .case(deleteMediaFile.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.deleteMediaFile
    })
  })

  .case(deleteMediaFile.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })

  .case(deleteMediaFile.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.deleteMediaFile = (error as Response)
    })
  })

  /**
   * listMediaFiles
   */
  .case(listMediaFiles.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.listMediaFiles
    })
  })

  .case(listMediaFiles.done, (state, { result }) => {
    return produce(state, draft => {
      draft.mediaFiles = result
      draft.fetching = false
    })
  })

  .case(listMediaFiles.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.listMediaFiles = (error as Response)
    })
  })

  /**
   * getPageSummary
   */
  .case(setUrlInformation, (state, pageResource) => {
    return ({ ...state, pageResource })
  })
  .case(getUrlInformation.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getPageSummary
    })
  })

  .case(getUrlInformation.done, (state, { result }) => {
    return produce(state, draft => {
      draft.pageResource = result
      draft.fetching = false
    })
  })

  .case(getUrlInformation.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getPageSummary = (error as Response)
    })
  })

  /**
   * createTags
   */
  .case(createTags.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.createTags
    })
  })

  .case(createTags.done, (state, { result }) => {
    return produce(state, draft => {
      const newTags = draft.tags ? (draft.tags).concat(result) : result
      draft.tags = newTags
      draft.fetching = false
    })
  })

  .case(createTags.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.createTags = (error as Response)
    })
  })

  /**
   * updateDeferredPost
   */
  .case(updateDeferredPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.updateDeferredPost
    })
  })

  .case(updateDeferredPost.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      triggerProfessocialPostingSuccess(true)
    })
  })

  .case(updateDeferredPost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.updateDeferredPost = (error as Response)
    })
  })

  /**
   * updatePagePost
   */
  .case(updatePagePost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.updatePagePost
    })
  })

  .case(updatePagePost.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      triggerProfessocialPostingSuccess(true)
    })
  })

  .case(updatePagePost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.updatePagePost = (error as Response)
    })
  })

  /**
   * individualizeText
   */
  .case(individualizeText.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.individualizeText
    })
  })

  .case(individualizeText.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.individualizedText = result
    })
  })

  .case(individualizeText.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.individualizeText = (error as Response)
    })
  })

/**
 * getDeferredPostById
 */
  .case(getDeferredPostById.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getDeferredPostById
    })
  })
  .case(getDeferredPostById.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.deferredPostById = result
    })
  })

  .case(getDeferredPostById.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getDeferredPostById = (error as Response)
    })
  })
